import {
	faChevronLeft,
	faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalStorefrontItemCardDto } from '@supply-mover-2-0/shared';
import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { StorefrontItemCard } from './StorefrontItemCard';

/**
 * Horizontally scrolling row of storefront item cards. Native CSS scroll with
 * scroll-snap, so it needs no carousel library; the chevrons advance one
 * viewport at a time on desktop and are hidden on phones, where people swipe.
 *
 * Shared by the storefront and the CRM page builder (PD-16588). Navigation,
 * the cart control and image self-healing belong to the caller, so this
 * component has no router, query client or storefront store.
 */
export function ItemCarousel({
	items,
	emptyState,
	density = 'standard',
	showPrices,
	showImages = true,
	onSelectItem,
	renderCardActions,
	onLikelyStaleUrl,
}: {
	items: Array<PortalStorefrontItemCardDto>;
	emptyState: ReactNode;
	/**
	 * `compact` narrows each card on phones so about one more item fits per
	 * screen before swiping. Desktop is unchanged.
	 */
	density?: 'standard' | 'compact';
	showPrices: boolean;
	/**
	 * False drops each card's picture box, for brands that hide item images
	 * (PD-17144). Default true; the page builder canvas keeps the default.
	 */
	showImages?: boolean;
	onSelectItem?: (item: PortalStorefrontItemCardDto) => void;
	renderCardActions?: (item: PortalStorefrontItemCardDto) => ReactNode;
	/** Called when an item's signed image link has likely expired. */
	onLikelyStaleUrl?: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Arrow visibility follows the real layout (viewport width vs content
	// width), not the item count, so it is measured from the scroller.
	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		setCanScrollLeft(el.scrollLeft > 4);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateArrows();
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		el.addEventListener('scroll', updateArrows, { passive: true });
		window.addEventListener('resize', updateArrows);
		return (): void => {
			el.removeEventListener('scroll', updateArrows);
			window.removeEventListener('resize', updateArrows);
		};
	}, [updateArrows, items.length]);

	const scrollByViewport = (direction: 1 | -1): void => {
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		// A little under a full viewport, so a partial card on the leading
		// edge hints that there is more.
		el.scrollBy({
			left: el.clientWidth * 0.85 * direction,
			behavior: 'smooth',
		});
	};

	if (items.length === 0) {
		return <>{emptyState}</>;
	}

	return (
		<div className="relative w-full h-full">
			<div
				ref={scrollerRef}
				className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1 scrollbar-thin"
				style={{ scrollbarWidth: 'thin' }}
			>
				{items.map((item) => (
					<div
						key={item.id}
						className={`snap-start shrink-0 ${
							density === 'compact'
								? 'w-[110px] sm:w-[140px] md:w-[180px]'
								: 'w-[150px] sm:w-[170px] md:w-[180px]'
						}`}
					>
						<StorefrontItemCard
							item={item}
							showPrice={showPrices}
							showImage={showImages}
							onNavigate={
								onSelectItem != null
									? (): void => {
											onSelectItem(item);
										}
									: undefined
							}
							onLikelyStaleUrl={onLikelyStaleUrl}
							actions={renderCardActions?.(item)}
						/>
					</div>
				))}
			</div>

			{canScrollLeft && (
				<button
					type="button"
					onClick={() => {
						scrollByViewport(-1);
					}}
					aria-label={t('Scroll left')}
					className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-surface shadow-lg border border-brand-border hover:bg-brand-input z-10"
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</button>
			)}
			{canScrollRight && (
				<button
					type="button"
					onClick={() => {
						scrollByViewport(1);
					}}
					aria-label={t('Scroll right')}
					className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-surface shadow-lg border border-brand-border hover:bg-brand-input z-10"
				>
					<FontAwesomeIcon icon={faChevronRight} />
				</button>
			)}
		</div>
	);
}

/** Loading placeholder shaped like the item and category rows. */
export function ItemCarouselSkeleton({
	cards = 4,
	showImages = true,
}: {
	cards?: number;
	/** Mirrors the live cards' picture box so the row does not reflow (PD-17144). */
	showImages?: boolean;
}): JSX.Element {
	return (
		<div
			className="flex gap-3 overflow-hidden pb-2 -mx-1 px-1 animate-pulse"
			aria-hidden="true"
		>
			{Array.from({ length: cards }, (_, i) => (
				<div key={i} className="shrink-0 w-[150px] sm:w-[170px] md:w-[180px]">
					<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
						{showImages && <div className="aspect-square bg-brand-input" />}
						<div className="p-2 sm:p-3">
							<div className="h-3 bg-brand-input rounded mb-2 w-3/4" />
							<div className="h-3 bg-brand-input rounded w-1/2" />
						</div>
					</div>
				</div>
			))}
		</div>
	);
}
